import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MuiTable, TableCell } from "src/components/MuiTable/MuiTable.tsx";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { formatDate, formatDateTime } from "@exonn/utils";
import type {
  ConditionResource,
  SupplierOrderReceiptResource,
  WarehouseResource,
} from "@exonn/js-sdk";
import { Panel } from "src/components/Panel/Panel.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Input } from "src/components/Input/Input.tsx";
import { Select } from "src/components/Select/Select.tsx";
import { Flex } from "src/components/Flex.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { useSelect } from "src/system/useSelect.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";
import type { SupplierOrderFormData } from "src/admin/modules/supplier-order/forms/supplier-order/config.tsx";
import { useSupplierOrderAction } from "src/admin/modules/supplier-order/forms/supplier-order/hooks/useSupplierOrderAction.ts";
import { CorrectReceiptDialog } from "src/admin/modules/supplier-order/forms/supplier-order/tabs/receipt/CorrectReceiptDialog.tsx";
import { PlaceSelect } from "src/admin/modules/supplier-order/forms/supplier-order/tabs/receipt/PlaceSelect.tsx";

// place_id пустой — у строки на Ziellager берётся место по умолчанию (над таблицей).
type Line = {
  quantity: string;
  best_before_date: string;
  warehouse_id: string;
  place_id: string;
};

const receivable = ["sent", "confirmed", "partially_received"];

/**
 * Wareneingang по заказу: количество (по умолчанию — открытое), склад, место и MHD по каждой позиции,
 * состояние товара — на весь приход. Склад по умолчанию — Ziellager заказа (плановый), место по умолчанию
 * на нём задаётся над таблицей; строку можно оприходовать и на другой склад. Допуск перепоставки, MHD
 * и место проверяет бэкенд; ошибка любой строки отменяет весь приход.
 * Ниже — проведённые приходы с исправлением количества (0 — сторно) и цены.
 */
export const Receipt = () => {
  const { t } = useResourceFormTranslation();
  // Цена прихода хранится в стандартной валюте (пересчёт по курсу заказа), не в валюте заказа.
  const {
    data: { default_currency },
  } = useGlobalSettings();
  const data = useResourceFormData<SupplierOrderFormData>();
  const order = data?.data;
  const exonnApi = useExonnApi();
  const { mutate, isPending } = useSupplierOrderAction(order?.id);
  const [placeId, setPlaceId] = useState("");
  const [conditionId, setConditionId] = useState("");
  const [lines, setLines] = useState<Record<string, Line>>({});
  const [correcting, setCorrecting] =
    useState<SupplierOrderReceiptResource | null>(null);

  const { options: warehouseOptions } = useSelect<WarehouseResource>({
    resource: ResourceEnum.Warehouse,
    labelProp: "title",
  });
  const { options: conditionOptions } = useSelect<ConditionResource>({
    resource: ResourceEnum.Condition,
    labelProp: "title",
  });

  const receipts = useQuery({
    queryKey: [
      ResourceEnum.SupplierOrder,
      order?.id,
      "receipts",
      order?.version,
      order?.updated_at,
    ],
    enabled: !!order?.id,
    queryFn: async () =>
      (
        await exonnApi.client.get<{ data: SupplierOrderReceiptResource[] }>(
          `/supplier-order/${order?.id}/receipts`,
        )
      ).data.data,
  });

  if (!order?.id) {
    return null;
  }

  const open = (order.positions ?? []).filter(
    (position) => Number(position.quantity_open) > 0,
  );
  const plannedWarehouseId = order.warehouse_id ?? "";
  const lineOf = (id: string, fallback: string): Line =>
    lines[id] ?? {
      quantity: fallback,
      best_before_date: "",
      warehouse_id: plannedWarehouseId,
      place_id: "",
    };
  const placeOf = (line: Line) =>
    line.place_id || (line.warehouse_id === plannedWarehouseId ? placeId : "");
  const setLine = (id: string, fallback: string, patch: Partial<Line>) =>
    setLines((previous) => ({
      ...previous,
      [id]: { ...lineOf(id, fallback), ...patch },
    }));

  const toReceive = open
    .map((position) => ({
      position,
      line: lineOf(position.id, String(Number(position.quantity_open))),
    }))
    .filter(({ line }) => Number(line.quantity) > 0);

  const receive = () =>
    mutate(
      {
        url: `/supplier-order/${order.id}/receive`,
        body: {
          lines: toReceive.map(({ position, line }) => ({
            position_id: position.id,
            quantity: line.quantity,
            warehouse_place_id: placeOf(line),
            condition_id: conditionId,
            best_before_date: line.best_before_date || null,
          })),
        },
      },
      { onSuccess: () => setLines({}) },
    );

  const titleOf = (positionId: string | null) =>
    order.positions?.find((position) => position.id === positionId)?.title ??
    "";

  return (
    <div style={{ padding: 15 }}>
      {receivable.includes(order.status) && (
        <Panel>
          <Panel.Header>
            <Panel.Title>
              <Panel.Icon>
                <span className={"icon-15-basic_info"} />
              </Panel.Icon>
              {t("receipt.title")}
            </Panel.Title>
          </Panel.Header>
          <Panel.Content>
            {open.length === 0 ? (
              <TextV2 variant={"body1"}>{t("receipt.nothing-open")}</TextV2>
            ) : (
              <>
                <Flex gap={"10px"} style={{ marginBottom: 15 }}>
                  <div style={{ flex: 1 }}>
                    <PlaceSelect
                      label={t("receipt.default-place", {
                        warehouse: order.warehouse?.title ?? "",
                      })}
                      warehouseId={plannedWarehouseId}
                      value={placeId}
                      onChange={setPlaceId}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Select
                      label={t("receipt.condition")}
                      value={conditionId}
                      onChange={(value) => setConditionId(value ?? "")}
                      options={conditionOptions}
                    />
                  </div>
                </Flex>
                <MuiTable size={"small"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("positions.position")}</TableCell>
                      <TableCell>{t("positions.supplier-sku")}</TableCell>
                      <TableCell>{t("positions.title-column")}</TableCell>
                      <TableCell>{t("positions.open")}</TableCell>
                      <TableCell>{t("receipt.quantity")}</TableCell>
                      <TableCell>{t("receipt.warehouse")}</TableCell>
                      <TableCell>{t("receipt.place")}</TableCell>
                      <TableCell>{t("receipt.best-before-date")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {open.map((position) => {
                      const fallback = String(Number(position.quantity_open));
                      const line = lineOf(position.id, fallback);
                      return (
                        <TableRow key={position.id}>
                          <TableCell>{position.position}</TableCell>
                          <TableCell>{position.supplier_sku}</TableCell>
                          <TableCell>{position.title}</TableCell>
                          <TableCell>
                            {Number(position.quantity_open)}
                          </TableCell>
                          <TableCell style={{ width: 120 }}>
                            <Input
                              type={"number"}
                              min={0}
                              value={line.quantity}
                              onChange={(event) =>
                                setLine(position.id, fallback, {
                                  quantity: event.target.value,
                                })
                              }
                            />
                          </TableCell>
                          <TableCell style={{ width: 200 }}>
                            <Select
                              value={line.warehouse_id}
                              onChange={(value) =>
                                setLine(position.id, fallback, {
                                  warehouse_id: value ?? plannedWarehouseId,
                                  place_id: "",
                                })
                              }
                              options={warehouseOptions}
                              allowClearValue={false}
                              showSearch
                            />
                          </TableCell>
                          <TableCell style={{ width: 200 }}>
                            <PlaceSelect
                              warehouseId={line.warehouse_id}
                              value={line.place_id}
                              onChange={(value) =>
                                setLine(position.id, fallback, {
                                  place_id: value,
                                })
                              }
                              placeholder={
                                line.warehouse_id === plannedWarehouseId
                                  ? t("receipt.place-as-default")
                                  : undefined
                              }
                            />
                          </TableCell>
                          <TableCell style={{ width: 160 }}>
                            <Input
                              type={"date"}
                              value={line.best_before_date}
                              onChange={(event) =>
                                setLine(position.id, fallback, {
                                  best_before_date: event.target.value,
                                })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </MuiTable>
                <Flex justifyContent={"flex-end"} style={{ marginTop: 15 }}>
                  <Button
                    variant={"filled"}
                    disabled={
                      isPending ||
                      conditionId === "" ||
                      toReceive.length === 0 ||
                      toReceive.some(({ line }) => placeOf(line) === "")
                    }
                    onClick={receive}
                  >
                    {t("receipt.book", { count: toReceive.length })}
                  </Button>
                </Flex>
              </>
            )}
          </Panel.Content>
        </Panel>
      )}
      <div style={{ height: 15 }} />
      <Panel>
        <Panel.Header>
          <Panel.Title>
            <Panel.Icon>
              <span className={"icon-15-basic_info"} />
            </Panel.Icon>
            {t("receipt.history")}
          </Panel.Title>
        </Panel.Header>
        <Panel.Content>
          {(receipts.data ?? []).length === 0 ? (
            <TextV2 variant={"body1"}>{t("receipt.history-empty")}</TextV2>
          ) : (
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("receipt.date")}</TableCell>
                  <TableCell>{t("positions.title-column")}</TableCell>
                  <TableCell>{t("receipt.quantity")}</TableCell>
                  <TableCell>
                    {t("receipt.price", { currency: default_currency?.iso })}
                  </TableCell>
                  <TableCell>{t("receipt.warehouse")}</TableCell>
                  <TableCell>{t("receipt.place")}</TableCell>
                  <TableCell>{t("receipt.best-before-date")}</TableCell>
                  <TableCell>{t("history.user")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {(receipts.data ?? []).map((receipt) => (
                  <TableRow key={receipt.stock_move_id}>
                    <TableCell>{formatDateTime(receipt.created_at)}</TableCell>
                    <TableCell>
                      {titleOf(receipt.supplier_order_position_id)}
                    </TableCell>
                    <TableCell>
                      {Number(receipt.amount).toLocaleString("de-DE")}
                    </TableCell>
                    <TableCell>
                      {Number(receipt.purchasing_price ?? 0).toLocaleString(
                        "de-DE",
                        { minimumFractionDigits: 2, maximumFractionDigits: 6 },
                      )}
                    </TableCell>
                    <TableCell>{receipt.warehouse ?? ""}</TableCell>
                    <TableCell>{receipt.warehouse_place ?? ""}</TableCell>
                    <TableCell>
                      {receipt.best_before_date
                        ? formatDate(receipt.best_before_date)
                        : "—"}
                    </TableCell>
                    <TableCell>{receipt.user?.name ?? ""}</TableCell>
                    <TableCell>
                      {["partially_received", "received"].includes(
                        order.status,
                      ) && (
                        <Button
                          size={"sm"}
                          variant={"outlined"}
                          onClick={() => setCorrecting(receipt)}
                        >
                          {t("receipt.correct.button")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          )}
        </Panel.Content>
      </Panel>
      {correcting && (
        <CorrectReceiptDialog
          receipt={correcting}
          isPending={isPending}
          onSubmit={(body) =>
            mutate(
              {
                url: `/supplier-order/${order.id}/receipts/${correcting.stock_move_id}/correct`,
                body,
              },
              { onSuccess: () => setCorrecting(null) },
            )
          }
          onClose={() => setCorrecting(null)}
        />
      )}
    </div>
  );
};
